import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { appConfirm } from "../../components/ConfirmDialog";

export function QuoteNotes({ quoteId, data, onNotesChange }) {
  const [draft, setDraft] = useState(data.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [picked, setPicked] = useState([]);
  const noteRef = useRef(null);

  useEffect(() => {
    api.get("/admin/mentionable").then(r => setStaffList(r.data.items || [])).catch(() => {});
  }, [quoteId]);

  const onDraftChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    const upto = val.slice(0, e.target.selectionStart);
    const m = upto.match(/@([\w.\-']*)$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const mentionMatches = mentionQuery == null ? [] : staffList
    .filter(s => (s.name || "").toLowerCase().includes(mentionQuery) || (s.email || "").toLowerCase().includes(mentionQuery))
    .slice(0, 6);

  const pickMention = (s) => {
    const el = noteRef.current;
    const caret = el ? el.selectionStart : draft.length;
    const before = draft.slice(0, caret).replace(/@([\w.\-']*)$/, `@${s.name} `);
    setDraft(before + draft.slice(caret));
    setPicked(p => p.some(x => x.email === s.email) ? p : [...p, s]);
    setMentionQuery(null);
    if (el) setTimeout(() => el.focus(), 0);
  };

  const addNote = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    try {
      const mentions = picked.filter(p => body.includes(`@${p.name}`)).map(p => p.email);
      const r = await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/note`, { body, mentions });
      setDraft("");
      setPicked([]);
      setMentionQuery(null);
      onNotesChange([...(data.notes || []), r.data]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add note"); }
    setSaving(false);
  };

  const deleteNote = async (noteId) => {
    if (!(await appConfirm({ title: "Delete note?", description: "Delete this note?", confirmLabel: "Delete", destructive: true }))) return;
    try {
      await api.delete(`/admin/quote-note/${encodeURIComponent(noteId)}`);
      onNotesChange((data.notes || []).filter(n => n.id !== noteId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete note"); }
  };

  return (
    <div className="space-y-4" data-testid="quote-detail-notes">
      {data.admin_notes && (
        <details className="border border-[#F0EAE0] bg-[#F8F5F0] p-3">
          <summary className="text-xs font-bold uppercase tracking-widest text-[#6B7280] cursor-pointer hover:text-[#12263A]">
            Archived note (legacy) · last saved {data.admin_notes_updated_at ? new Date(data.admin_notes_updated_at).toLocaleString("en-GB") : "—"}{data.admin_notes_updated_by ? ` by ${data.admin_notes_updated_by}` : ""}
          </summary>
          <div className="text-sm text-[#374151] mt-2 whitespace-pre-wrap">{data.admin_notes}</div>
        </details>
      )}

      <div className="border border-[#E5E7EB] bg-white p-3">
        <label className="label-eyebrow block mb-2">Add a note</label>
        <div className="relative">
          <textarea ref={noteRef} data-testid="quote-detail-note-body" value={draft} onChange={onDraftChange} rows={3}
            placeholder="Log a call, a follow-up, a mention from the survey — everyone on the team will see this on the file."
            className="w-full border border-[#E5E7EB] px-3 py-2 text-sm resize-y"/>
          {mentionMatches.length > 0 && (
            <div data-testid="quote-detail-mention-dropdown" className="absolute left-0 top-full -mt-1 z-30 min-w-[240px] bg-white border border-[#E5E7EB] shadow-lg py-1">
              {mentionMatches.map(s => (
                <button key={s.email} type="button" data-testid={`mention-option-${s.email}`}
                  onMouseDown={(e) => { e.preventDefault(); pickMention(s); }}
                  className="block w-full text-left px-3 py-1.5 hover:bg-[#FBF3EC]">
                  <span className="text-xs font-bold text-[#12263A]">@{s.name}</span>
                  <span className="text-[11px] text-[#6B7280] ml-2">{s.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-[11px] text-[#6B7280]">Type <b>@</b> to mention a teammate — they&apos;ll get a notification.</div>
          <button data-testid="quote-detail-note-add" onClick={addNote} disabled={saving || !draft.trim()}
            className="de-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
            <Plus size={13}/> {saving ? "Adding…" : "Add note"}
          </button>
        </div>
      </div>

      <div className="space-y-2" data-testid="quote-detail-notes-thread">
        {(data.notes || []).length === 0 && (
          <div className="text-sm text-[#6B7280] italic text-center py-8">No notes yet — write the first one above.</div>
        )}
        {(data.notes || []).slice().reverse().map(n => (
          <div key={n.id} className="border border-[#E5E7EB] bg-white p-3" data-testid={`quote-detail-note-${n.id}`}>
            <div className="flex items-baseline justify-between gap-2">
              <div className="text-xs font-bold text-[#12263A]">{n.author_name || n.author}</div>
              <div className="flex items-center gap-2">
                <div className="text-[10px] text-[#6B7280]">{new Date(n.created_at).toLocaleString("en-GB")}</div>
                <button data-testid={`quote-detail-note-delete-${n.id}`} onClick={() => deleteNote(n.id)} className="text-[#6B7280] hover:text-[#C0392B]" title="Delete note">
                  <Trash2 size={11}/>
                </button>
              </div>
            </div>
            <p className="text-sm mt-1.5 text-[#374151] whitespace-pre-wrap"><NoteBody note={n}/></p>
          </div>
        ))}
      </div>
    </div>
  );
}

function NoteBody({ note }) {
  const names = (note.mentions || []).map(m => m.name).filter(Boolean);
  if (!names.length) return note.body;
  const re = new RegExp(`@(${names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  return note.body.split(re).map((part, i) =>
    names.includes(part)
      ? <span key={`${i}-${part}`} className="text-[#C79A3B] font-bold">@{part}</span>
      : part
  );
}
